import { useState, useEffect } from 'react';
import { Form, message, Modal } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { shippingAPI } from '../../../../services/shippingService';
import type { ShippingRateTier } from '../../../../types/shipping-types';

export const useShippingChannel = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [rateTiers, setRateTiers] = useState<ShippingRateTier[]>([]);
    const [editingTier, setEditingTier] = useState<Partial<ShippingRateTier> | null>(null);
    const [tierModalVisible, setTierModalVisible] = useState(false);
    const [tierForm] = Form.useForm();

    const isEditing = !!id;

    const loadChannel = async () => {
        setLoading(true);
        try {
            const response = await shippingAPI.getChannel(parseInt(id!));
            const channel = response.data;
            form.setFieldsValue(channel);
            setRateTiers(channel.rate_tiers || []);
        } catch (error) {
            message.error('加载物流渠道失败');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isEditing) {
            loadChannel();
        }
    }, [id]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (isEditing) {
                await shippingAPI.updateChannel(parseInt(id!), values);
                message.success('更新成功');
            } else {
                const response = await shippingAPI.createChannel(values);
                message.success('创建成功');
                navigate(`/shipping-management/edit/${response.data.id}`);
            }
        } catch (error) {
            message.error('保存失败');
        } finally {
            setLoading(false);
        }
    };

    const handleAddTier = () => {
        setEditingTier(null);
        tierForm.resetFields();
        setTierModalVisible(true);
    };

    const handleEditTier = (tier: ShippingRateTier) => {
        setEditingTier(tier);
        tierForm.setFieldsValue(tier);
        setTierModalVisible(true);
    };

    const handleSaveTier = async () => {
        try {
            const values = await tierForm.validateFields();

            if (editingTier?.id) {
                await shippingAPI.updateRateTier(editingTier.id, values);
                message.success('更新成功');
            } else {
                if (!id) {
                    message.error('请先保存物流渠道');
                    return;
                }
                await shippingAPI.createRateTier({ ...values, channel: parseInt(id) });
                message.success('添加成功');
            }

            setTierModalVisible(false);
            loadChannel();
        } catch (error) {
            message.error('保存失败');
        }
    };

    const handleDeleteTier = (tierId: number) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个运费阶梯吗？',
            onOk: async () => {
                try {
                    await shippingAPI.deleteRateTier(tierId);
                    message.success('删除成功');
                    loadChannel();
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    return {
        form,
        loading,
        rateTiers,
        editingTier,
        tierModalVisible,
        setTierModalVisible,
        tierForm,
        isEditing,
        handleSubmit,
        handleAddTier,
        handleEditTier,
        handleSaveTier,
        handleDeleteTier,
        navigate,
    };
};
